import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "components/app/dialog/dialog";
import { QrCode } from "lucide-react";
import { QRCode } from "react-qrcode-logo";
import { cn } from "lib/utils";
import {
  backgroundQrProfile,
  backgroundQrProfile1,
  backgroundQrProfile2,
  backgroundQrProfile3,
  backgroundQrProfile4,
  backgroundQrProfile5,
  backgroundQrProfile6,
  backgroundQrProfile7,
  backgroundQrProfile8,
  backgroundQrProfile9,
  backgroundQrProfile10,
  logoFlipFlop,
  noneProfile,
} from "assets";
import {
  faArrowLeft,
  faArrowRight,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/free-regular-svg-icons";
import { useRef, useState } from "react";
import useClipboard from "react-use-clipboard";
import html2canvas from "html2canvas";
import { saveAs } from "file-saver";

export default function QrCodeGenerater({
  className,
  linkUrl,
  linkUrlClipboard,
  user,
}: {
  className?: string;
  linkUrl: string;
  linkUrlClipboard?: any;
  user: { thumbnail?: string };
}) {
  const elementRef = useRef<HTMLDivElement>(null);
  const bgQrUserProfile = [
    { id: 1, bgSrc: backgroundQrProfile1 },
    { id: 2, bgSrc: backgroundQrProfile2 },
    { id: 3, bgSrc: backgroundQrProfile3 },
    { id: 4, bgSrc: backgroundQrProfile4 },
    { id: 5, bgSrc: backgroundQrProfile5 },
    { id: 6, bgSrc: backgroundQrProfile6 },
    { id: 7, bgSrc: backgroundQrProfile7 },
    { id: 8, bgSrc: backgroundQrProfile8 },
    { id: 9, bgSrc: backgroundQrProfile9 },
    { id: 10, bgSrc: backgroundQrProfile10 },
  ];
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isDownloadQr, setIsDownloadQr] = useState<boolean>(false);

  const imageUrlUserProfile = user?.thumbnail?.startsWith("http://")
    ? user?.thumbnail.replace("https://", "http://")
    : user?.thumbnail;

  const previousBackground = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? bgQrUserProfile.length - 1 : prevIndex - 1
    );
  };

  const nextBackground = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === bgQrUserProfile.length - 1 ? 0 : prevIndex + 1
    );
  };

  const [isCopied, setCopied] = useClipboard(linkUrlClipboard, {
    successDuration: 1000,
  });

  const downloadQRMenu = async () => {
    setIsDownloadQr(true);
    if (elementRef.current) {
      try {
        // Set styles dynamically
        const element = elementRef.current;
        await new Promise((resolve) => setTimeout(resolve, 1000));
        // Render to canvas
        const canvas = await html2canvas(element, {
          useCORS: false,
          allowTaint: false,
          logging: false,
          scale: 2,
          imageTimeout: 0,
          proxy: `${process.env.REACT_APP_API_URL}/get-image-url`,
        });
        // Convert canvas to Blob and download
        canvas.toBlob((blob) => {
          if (blob) saveAs(blob, "qr-user-profile.png");
        }, "image/png");
      } catch (error) {
        console.error("Error generating QR code:", error);
      } finally {
        setIsDownloadQr(false);
      }
    }
  };

  return (
    <Dialog>
      <DialogTrigger className={cn("cursor-pointer", className)} asChild>
        <QrCode />
      </DialogTrigger>
      <DialogContent className="">
        <DialogTitle></DialogTitle>
        <div ref={elementRef} className="aspect-[1454/2048] flex flex-col">
          <div
            style={{
              backgroundImage: `url("${bgQrUserProfile[currentIndex].bgSrc}")`,
            }}
            className="fixed top-0 left-0 right-10 bg-cover h-full w-full rounded-md"
          />
          <div
            className={`${
              isDownloadQr ? "hidden" : "block"
            } absolute space-x-2 -top-2 left-6 flex justify-center mt-4 z-50`}
          >
            <button
              onClick={previousBackground}
              className="w-[30px] h-[30px] text-gray-700 bg-white hover:bg-gray-200 rounded-full"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <button
              onClick={nextBackground}
              className="w-[30px] h-[30px] text-gray-700 bg-white hover:bg-gray-200 rounded-full"
            >
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
          <div
            className={`${
              isDownloadQr ? "hidden" : "block"
            } absolute space-x-2 -top-2 right-16 flex justify-center mt-4 z-50`}
          >
            <button
              className="w-[30px] h-[30px] text-gray-700 bg-white hover:bg-gray-200 rounded-full"
              onClick={setCopied}
            >
              <FontAwesomeIcon icon={faCopy} />
            </button>
            {isCopied && (
              <span className="absolute top-10 right-8 text-green-500 text-xs">
                Copied!
              </span>
            )}
            <button
              onClick={downloadQRMenu}
              className="w-[30px] h-[30px] text-gray-700 bg-white hover:bg-gray-200 rounded-full"
              aria-label="Download QR Code"
            >
              <FontAwesomeIcon icon={faDownload} />
            </button>
          </div>
          <div className="relative h-full flex justify-center items-center">
            <div
              className="relative flex items-center justify-center bg-white rounded-xl"
              style={{
                padding: 5,
                paddingTop: 40,
              }}
            >
              <QRCode
                size={240}
                ecLevel="Q"
                qrStyle="squares"
                value={linkUrl}
              />
              <div
                className="absolute"
                style={{
                  top: "50%",
                  left: "42%",
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: "white",
                  overflow: "hidden",
                  zIndex: 50,
                }}
              >
                <img
                  src={logoFlipFlop}
                  className="rounded-full z-50"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                  alt="flipfloplogo"
                />
              </div>
              <div
                className="absolute"
                style={{
                  top: "-40px",
                  left: "35%",
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  backgroundColor: "white",
                  overflow: "hidden",
                  padding: 5,
                  zIndex: 50,
                }}
              >
                <img
                  src={imageUrlUserProfile ?? noneProfile}
                  className="rounded-full z-50"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                  alt="user-picture"
                />
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
